import { gql } from '@apollo/client';
import { brexitProductInfoFieldsCustomsDeclarationFragment } from '../../../../libs/getProductIdentifiersFromEntry/getProductIdentifiersFromCustomsDeclaration.query';

const recordActionFieldsFragment = gql`
  fragment recordActionFields on RecordAction {
    source {
      __typename
      ... on InternalSystem {
        internalSystemName: name
      }
      ... on InternalWebsite {
        internalWebsiteName: name
      }
      ... on ClientSystem {
        clientSystemName: name
      }
    }
    time
    reason {
      code
      text
    }
    client {
      id
    }
    request {
      ipAddress
      userAgent
      requestId
    }
  }
`;

const recordFieldsFragment = gql`
  fragment recordFields on Record {
    created {
      ...recordActionFields
    }
    updated {
      ...recordActionFields
    }
    deleted {
      ...recordActionFields
    }
    id
    version
    tags {
      key
      value
    }
  }
`;

const fragments = gql`
  fragment memoRecordActionFields on RecordAction {
    source {
      __typename
      ... on InternalSystem {
        internalSystemName: name
      }
      ... on InternalWebsite {
        internalWebsiteName: name
      }
      ... on ClientSystem {
        clientSystemName: name
      }
    }
    time
    reason {
      code
      text
    }
    client {
      id
      name
    }
    request {
      ipAddress
      userAgent
      requestId
    }
  }

  fragment memoFields on Memo {
    text
    isPublic
    operator {
      id
    }
    created {
      ...memoRecordActionFields
    }
    updated {
      ...memoRecordActionFields
    }
    deleted {
      ...memoRecordActionFields
    }
    id
    version
    tags {
      key
      value
    }
  }

  ${recordActionFieldsFragment}
  ${recordFieldsFragment}
`;

const taskFields = gql`
  fragment taskFields on WorkOrderTask {
    ...recordFields
    name
    workOrderType
    status
    assignee {
      id
      name
    }
    assignmentTeam {
      name
      id
      backupTeam {
        name
        id
      }
    }
    deadline
    steps {
      id
      name
      description
      isCompletedBySystem
      isFinalConfirmation
      completed {
        ...recordActionFields
      }
    }
    customFields {
      label
      customFieldType
      textValue
      enumValues
      enumOptions
    }
    definition {
      id
      estimatedDuration
      workOrderTaskType
      customFields {
        label
        customFieldType
        enumOptions
      }
    }
  }
  ${recordActionFieldsFragment}
  ${recordFieldsFragment}
`;

export const goodsItemFragment = gql`
  fragment goodsItemFields on GoodsItem {
    sequence
    transactionNature
    customsValue {
      value
      currency
    }
    goodsMeasure {
      grossWeight {
        value
        unit
      }
      netNetWeight {
        value
        unit
      }
      tariff
    }
    commodity {
      description
      classification {
        identification
        identificationType
        sequence
      }
      dutyTaxFees {
        type
        payment {
          payment {
            value
            currency
          }
        }
      }
      product {
        id
        version
      }
    }
    origin {
      country
      type
    }
    packaging {
      type
      quantity
      marksNumbers
      sequence
    }
    customsValuation {
      method
      freightCharge {
        value
        currency
      }
      insurance {
        value
        currency
      }
      chargeDeduction {
        chargesType
        otherChargeDeduction {
          value
          currency
        }
        sequence
      }
    }
    valuationAdjustment {
      addition
      amount {
        value
        currency
      }
      percentage
    }
    additionalReference {
      identification
      type
    }
    governmentProcedure {
      current
      previous
      description
      additionalProcedure {
        procedure
        sequence
      }
    }
    seller {
      id
      name
    }
  }
`;

export const goodsShipmentFragment = gql`
  fragment goodsShipmentFields on GoodsShipment {
    importer {
      id
      name
    }
    importCountry {
      country
    }
    exportCountry {
      country
    }
    tradeTerms {
      condition
    }
    goodsItem {
      ...goodsItemFields
    }
    consignment {
      value
      borderTransportationMean {
        identificationType
        journey
        name
        arrival
        arrivalLocation {
          identification
        }
        departure
        departureLocation {
          identification
        }
      }
      transportContractDocument {
        identification
        type
      }
      transportEquipment {
        fullness
        identification
      }
    }
  }
  ${goodsItemFragment}
`;

export const queryCustomsDeclaration = gql`
  query CustomsDeclaration($id: ID!) {
    customsDeclaration(id: $id) {
      ...brexitProductInfoFieldsCustomsDeclaration
      id
      identification
      version
      status
      deadline
      badges
      canceled {
        reason {
          text
        }
      }
      operator {
        id
      }
      confirmations {
        id
        status
      }
      goodsShipment {
        ...goodsShipmentFields
      }
      stateMachine {
        states {
          taskDefinition {
            id
            version
            workOrderTaskType
          }
        }
      }
      milestones {
        tags {
          key
          value
        }
        id
        name
        occurenceTime
        source
        created {
          client {
            name
          }
        }
      }
      tasks {
        ...taskFields
      }
      group {
        documents {
          documentTags
        }
        customsBroker {
          tags {
            key
            value
          }
        }
        forwarders {
          forwarder {
            id
            name
            billingDetails {
              disbursement
              status
              subscriptionPlan
            }
            tags {
              key
              value
            }
          }
        }
        shipment {
          id
          clientReferences {
            bookingId
          }
          legs {
            transportationServiceName
          }
          preferredDutyCollectPartyType
        }
        invoices {
          id
        }
        id
        memos {
          ...memoFields
        }
        shipper {
          id
          shipperExternallySourcedDetails {
            externalReferenceId
          }
          billingDetails {
            disbursement
            status
            subscriptionPlan
          }
          memos {
            ...memoFields
          }
        }
      }
    }
  }
  ${fragments}
  ${taskFields}
  ${goodsShipmentFragment}
  ${brexitProductInfoFieldsCustomsDeclarationFragment}
`;

export const updateCustomsDeclaration = gql`
  mutation UpdateCustomsDeclaration(
    $id: ID!
    $version: Int!
    $input: UpdateCustomsDeclarationInput!
  ) {
    updateCustomsDeclaration(input: $input, version: $version, id: $id) {
      record {
        id
        version
        goodsShipment {
          ...goodsShipmentFields
        }
        tasks {
          ...taskFields
        }
      }
    }
  }
  ${taskFields}
  ${goodsShipmentFragment}
`;

export const createWorkOrderTaskMutation = gql`
  mutation createWorkOrderTask(
    $idempotencyKey: String!
    $input: CreateWorkOrderTaskInput!
  ) {
    createWorkOrderTask(idempotencyKey: $idempotencyKey, input: $input) {
      record {
        id
      }
    }
  }
`;
